import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTranscriptionEnabled } from '../hooks/useTranscriptionEnabled';
import {
  filesApi,
  RequestError,
  voiceJobsApi,
  voiceTopicsApi,
  type PresignResponse,
  type CreateVoiceJobResponse,
  type VoiceTopicResponse,
} from '../api/client';
import { createSilenceTracker, rmsFromTimeDomain } from '../utils/silenceDetector';
import {
  outcomeBeforePlaying,
  readTopicAudioMuted,
  recordedMs,
  voiceJobAudioFields,
  writeTopicAudioMuted,
  type TopicAudioOutcome,
  type TopicAudioOutcomes,
  type TopicAudioSlot,
} from '../utils/topicAudio';
import {
  createVoiceActivityMeter,
  isMostlySilent,
  resolveVoiceCheckFlag,
  MIN_MEASURED_MS,
  VOICED_RATIO_WARN_BELOW,
  type VoiceActivityStats,
} from '../utils/voiceActivity';
import {
  startCspViolationWatch,
  buildUploadFailureDetail,
} from '../utils/uploadDiagnostics';

const MIN_DURATION_SEC = 20;
const MAX_DURATION_SEC = 60;

/** お題カードを出すまでの無音の長さ(ms)。課題 #60 で 2 秒と決めている */
/**
 * 録音画面のメモ欄を出すか（課題 #145）。メモを録音に結び付けて保存する作り（段階 1）ができるまで false。
 * 今のメモ欄は送信に含まれず、書いた内容が保存されずに消えていたため
 */
const SHOW_RECORD_MEMO = false;


const TOPIC_SHOW_SILENCE_MS = 2000;
/**
 * 「録音開始」を押した操作の中で流しておく、ごく短い無音（課題 #160）。
 * iPhone では、画面を押した操作の中で一度鳴らしておかないと、後からお題の音声を自動で流せない
 */
const SILENCE_AUDIO_URL = '/audio/silence.wav';

/** 保存領域が使えない環境（プライベートブラウズなど）では null */
function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** お題の音声をどこで流しているか。録音中は録音を一時停止する。「もう少し話す」の画面では録音していない */
type TopicAudioTarget = 'recording' | 'retry';
/** 話し始めたあと、追い質問を継ぎ足すまでの無音の長さ(ms) */
const TOPIC_FOLLOW_UP_SILENCE_MS = 5000;
/**
 * 手動停止されたときに追い質問を添えて録り直しを促す上限(秒)。課題 #60 ①。
 * 停止そのものは覆さない（利用者が押した操作を打ち消すと「止まらないアプリ」になる）。
 */
const TOPIC_STOP_FOLLOW_UP_SEC = 15;
/** BE の音声認識（Google STT）はこの秒数を超える録音をスキップする（transcript_text が null になる） */
const TRANSCRIPT_MAX_DURATION_SEC = 58;
const UPLOAD_TIMEOUT_MS = 120_000;

const LEVEL_BARS = 12;
const LEVEL_SMOOTH = 0.25;

type SubmitStage = 'presign' | 'upload' | 'complete' | 'voice_jobs';

const STAGE_LABELS: Record<SubmitStage, string> = {
  presign: 'presign',
  upload: 'put(upload)',
  complete: 'complete',
  voice_jobs: 'voice-jobs',
};

function formatStageError(stage: SubmitStage, message: string) {
  return `[${STAGE_LABELS[stage]}] ${message}`;
}

function getAnalysisSummaryText(analysisResult: unknown): string | null {
  if (analysisResult == null) return null;
  if (typeof analysisResult === 'string') {
    const trimmed = analysisResult.trim();
    return trimmed !== '' ? trimmed : null;
  }
  if (typeof analysisResult !== 'object') return null;

  const result = analysisResult as Record<string, unknown>;
  const summary = result.summary;
  if (typeof summary === 'string') {
    const trimmed = summary.trim();
    return trimmed !== '' ? trimmed : null;
  }
  if (summary && typeof summary === 'object') {
    const summaryObj = summary as Record<string, unknown>;
    const text = summaryObj.text;
    if (typeof text === 'string' && text.trim() !== '') {
      return text.trim();
    }
  }

  const comment = result.comment;
  if (typeof comment === 'string' && comment.trim() !== '') {
    return comment.trim();
  }
  return null;
}

function getAnalysisTrendLabels(analysisResult: unknown): string[] {
  if (!analysisResult || typeof analysisResult !== 'object') return [];

  const result = analysisResult as Record<string, unknown>;
  const labels: string[] = [];
  const pushLabel = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (trimmed !== '') labels.push(trimmed);
  };
  const pushFromArray = (value: unknown) => {
    if (!Array.isArray(value)) return;
    value.forEach((item) => {
      if (typeof item === 'string') {
        pushLabel(item);
        return;
      }
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        pushLabel(obj.label);
        pushLabel(obj.tag);
        pushLabel(obj.name);
      }
    });
  };

  pushFromArray(result.trends);
  pushFromArray(result.labels);

  const shortTermBins = result.short_term_bins;
  pushFromArray(shortTermBins);
  if (shortTermBins && typeof shortTermBins === 'object' && !Array.isArray(shortTermBins)) {
    Object.keys(shortTermBins).forEach((key) => pushLabel(key));
  }

  const summary = result.summary;
  if (summary && typeof summary === 'object') {
    const summaryObj = summary as Record<string, unknown>;
    pushFromArray(summaryObj.labels);
    pushFromArray(summaryObj.trends);
  }

  const uniqueLabels = Array.from(new Set(labels));
  return uniqueLabels.slice(0, 6);
}

async function uploadWithTimeout(
  uploadUrl: string,
  body: Blob,
  contentType: string,
  timeoutMs: number
) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body,
      headers: { 'Content-Type': contentType },
      signal: controller.signal,
    });
    return res;
  } finally {
    clearTimeout(timer);
  }
}

/** 録音中の音声レベルを棒グラフで表示（AnalyserNode は親でユーザー操作内に作成） */
function RecordingLevelMeter({ analyser }: { analyser: AnalyserNode }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const barColor = '#0d5c5c';

  useEffect(() => {
    const canvas = canvasRef.current;
    const c = canvas?.getContext('2d');
    if (!canvas || !c) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const barHeights = new Array(LEVEL_BARS).fill(0);
    let rafId = 0;
    let cancelled = false;

    canvas.width = 280;
    canvas.height = 48;
    const w = 280;
    const h = 48;
    const barW = (w - (LEVEL_BARS - 1) * 4) / LEVEL_BARS;
    const gap = 4;

    const draw = () => {
      if (cancelled || !canvasRef.current) return;
      rafId = requestAnimationFrame(draw);
      try {
        analyser.getByteFrequencyData(dataArray);
      } catch {
        return;
      }
      const step = Math.floor(bufferLength / LEVEL_BARS);
      for (let i = 0; i < LEVEL_BARS; i++) {
        const v = dataArray[i * step] ?? 0;
        const target = (v / 255) * h * 0.9;
        barHeights[i] = barHeights[i] * (1 - LEVEL_SMOOTH) + target * LEVEL_SMOOTH;
      }
      c.fillStyle = 'rgba(255,255,255,0.9)';
      c.fillRect(0, 0, w, h);
      c.fillStyle = barColor;
      for (let i = 0; i < LEVEL_BARS; i++) {
        const x = i * (barW + gap);
        const barH = Math.max(2, barHeights[i]);
        c.fillRect(x, h - barH, barW, barH);
      }
    };
    draw();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [analyser]);

  return (
    <div className="record-level-wrap" aria-hidden="true">
      <canvas ref={canvasRef} className="record-level-canvas" width={280} height={48} />
    </div>
  );
}

/** 録音用 MIME タイプ（対応ブラウザ順） */
function getRecordingMimeType(): string {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const t of types) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}

export default function RecordVoice() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  /**
   * 文字起こし（音声認識テキスト）の案内と欄を出すか（課題 #99。2026-10-01 高尾さん決定）。
   * 利用規約・プライバシーポリシーと実装が食い違っていたため、文字起こしは centric で同意を取り直した方
   * だけ行い、ほかは行わない。BE が本人ごとに返す transcription_enabled が true のときだけ出す
   * （BE PR #153。項目が無い・読めないときは出さない）。
   * - 録音の前の「…秒の録音で音声認識テキストも取得されます」: GET /v1/organization/consent-mode の値
   * - 送信の後の欄: その録音の POST /v1/voice-jobs の応答の値（テキストが返っても、false なら出さない）
   */
  const transcriptionEnabled = useTranscriptionEnabled();
  const [recordingTranscribed, setRecordingTranscribed] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingAnalyser, setRecordingAnalyser] = useState<AnalyserNode | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [durationSec, setDurationSec] = useState(0);
  const [showMemo, setShowMemo] = useState(false);
  const [memo, setMemo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  /** 送信フェーズ: アップロード中 or 解析中（ローディング文言の切り替え） */
  const [submitPhase, setSubmitPhase] = useState<'upload' | 'analyzing'>('upload');
  /** 同期モードで受け取った解析結果（そのまま画面表示に利用） */
  const [analysisResult, setAnalysisResult] = useState<unknown | null>(null);
  /** 音声認識テキスト（本人のみ閲覧可・6 か月後に BE 側で自動 NULL 化） */
  const [transcriptText, setTranscriptText] = useState<string | null>(null);
  /** 送信時の録音秒数。transcript_text が null のとき「長いため省略された」かどうか判定するのに使う */
  const [submittedDurationSec, setSubmittedDurationSec] = useState<number | null>(null);
  /** BE から recording が返ってきたかどうか（analysis_result != null と常に連動） */
  const [transcriptChecked, setTranscriptChecked] = useState(false);

  /**
   * 録音中に出す「お題」（課題 #60 / API #89）。
   * 話すことが思いつかず黙ってしまった人にだけ出す。既に話している人には出さない。
   */
  const [topic, setTopic] = useState<VoiceTopicResponse | null>(null);
  /**
   * カードを出しているか。無音 2 秒で true になり、**話し始めても消さない**。
   *
   * 以前は話し始めたら消していたが、実マイクでは発話後の環境音が閾値付近で
   * 揺れるため、表示と非表示が毎フレーム入れ替わってちらついた（課題 #60 の
   * 「表示が崩れる」）。読み終わる前に消えるという報告も同じ原因。
   * 消すきっかけは利用者が閉じたときと録音停止だけにする。
   */
  const [topicVisible, setTopicVisible] = useState(false);
  /** 利用者がカードを閉じたか。閉じたらその録音中は出さない */
  const [topicDismissed, setTopicDismissed] = useState(false);
  /**
   * この録音で、お題カードが一度でも実際に表示されたか（API #91 の topic_shown）。
   * topicVisible は話し始めると false に戻るため、送信時には使えない。
   * 取得しても詰まらなかった方には表示されないので、topic_id の有無とも一致しない。
   */
  const [topicShown, setTopicShown] = useState(false);
  /** 追い質問を継ぎ足したか。新しいカードは出さず、同じカードに一文足す */
  const [showFollowUp, setShowFollowUp] = useState(false);
  /** 「別のお題」を使ったか。1 録音につき 1 回まで（録音開始で戻す） */
  const [redrawUsed, setRedrawUsed] = useState(false);
  const [redrawing, setRedrawing] = useState(false);
  /**
   * お題の音声（課題 #160。2026-10-09 高尾さん決定）。音のオン・オフは端末ごとに覚える（初期値はオン）。
   * playingSlot は、今流している枠（お題か追い質問か）。流している間は録音を一時停止している
   */
  const [topicAudioMuted, setTopicAudioMuted] = useState(() => readTopicAudioMuted(safeLocalStorage()));
  const [playingSlot, setPlayingSlot] = useState<TopicAudioSlot | null>(null);

  /**
   * アップロード失敗時の切り分け情報（送信先ホストと CSP 違反の有無）。
   * 失敗したときだけ出す。通常時に出すと画面が技術的になりすぎる。
   */
  /** 直前の録音で声が入っていた時間の割合（課題 #98）。録音を止めた時点で確定する */
  const [voiceStats, setVoiceStats] = useState<VoiceActivityStats | null>(null);
  /**
   * この録音を送信し終えたか（課題 #99）。送信後も録音データは画面に残るため、
   * これが無いと送信ボタンが押せる状態に戻り、同じ録音が別の記録として二重に登録される。
   */
  const [submitted, setSubmitted] = useState(false);
  /** 発話割合を数値で出す確認表示（課題 #98）。CENTRIC の基準値決め用で、利用者には出さない */
  const [voiceCheck] = useState(() => {
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      /* 保存領域が使えない環境ではクエリの指定だけを見る */
    }
    return resolveVoiceCheckFlag(window.location.search, storage);
  });
  const [uploadFailureDetail, setUploadFailureDetail] = useState<{
    host: string;
    cspBlocked: boolean;
  } | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /*
   * お題の音声（課題 #160）。音声の部品は 1 つを使い回す（iPhone では、押した操作の中で一度鳴らした
   * 部品でないと、後から自動で音を出せないため）。
   * 一時停止していた時間は、録音の長さに数えない（pausedTotal と pausedSince で引く）。
   */
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const topicAudioMutedRef = useRef(topicAudioMuted);
  const playingRef = useRef<{ slot: TopicAudioSlot; id: number; target: TopicAudioTarget } | null>(null);
  const playSeqRef = useRef(0);
  const pausedTotalRef = useRef(0);
  const pausedSinceRef = useRef<number | null>(null);
  /** この録音の流れ方（送信に添える）と、「もう少し話す」の画面で流した分（次の録り直しに引き継ぐ） */
  const audioOutcomesRef = useRef<TopicAudioOutcomes>({});
  const retryOutcomesRef = useRef<TopicAudioOutcomes>({});
  /** 同じお題・追い質問を 1 回の録音で二度流さないための印 */
  const playedQuestionRef = useRef<string | null>(null);
  const playedFollowUpRef = useRef<string | null>(null);
  const playedRetryRef = useRef<string | null>(null);

  const ensureAudio = useCallback((): HTMLAudioElement => {
    if (!audioElRef.current) {
      const a = new Audio();
      a.preload = 'auto';
      audioElRef.current = a;
    }
    return audioElRef.current;
  }, []);

  const resumeRecordingAfterAudio = useCallback(() => {
    if (pausedSinceRef.current != null) {
      pausedTotalRef.current += Math.max(0, Date.now() - pausedSinceRef.current);
      pausedSinceRef.current = null;
    }
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'paused') {
      try {
        mr.resume();
      } catch {
        /* 再開できなくても、停止の操作で録音は確定できる */
      }
    }
  }, []);

  /** 流し終えた・止まった・流せなかったときの後始末。録音中なら続きから録る（ご指示のとおり、エラーは出さない） */
  const finishTopicAudio = useCallback(
    (id: number, outcome: TopicAudioOutcome, resume = true) => {
      const cur = playingRef.current;
      if (!cur || cur.id !== id) return;
      playingRef.current = null;
      setPlayingSlot(null);
      const store = cur.target === 'recording' ? audioOutcomesRef : retryOutcomesRef;
      store.current = { ...store.current, [cur.slot]: outcome };
      const a = audioElRef.current;
      if (a) {
        a.onended = null;
        a.onerror = null;
      }
      if (resume && cur.target === 'recording') resumeRecordingAfterAudio();
    },
    [resumeRecordingAfterAudio]
  );

  /** 流している音声を止める（「止めて話す」・カードを閉じる・音をオフ・録音を止める・別のお題など） */
  const stopTopicAudio = useCallback(
    (outcome: TopicAudioOutcome = 'interrupted', resume = true) => {
      const cur = playingRef.current;
      if (!cur) return;
      try {
        audioElRef.current?.pause();
      } catch {
        /* ignore */
      }
      finishTopicAudio(cur.id, outcome, resume);
    },
    [finishTopicAudio]
  );

  const playTopicAudio = useCallback(
    (slot: TopicAudioSlot, url: string | null | undefined, target: TopicAudioTarget) => {
      const mr = mediaRecorderRef.current;
      const canPause = target === 'retry' || (mr != null && typeof mr.pause === 'function');
      const before = outcomeBeforePlaying(url, topicAudioMutedRef.current, canPause);
      // 前の音声が流れていたら止める。録音中に続けて流すときは、録音は止めたままにする
      stopTopicAudio('interrupted', before != null);
      if (before != null || !url) {
        const store = target === 'recording' ? audioOutcomesRef : retryOutcomesRef;
        store.current = { ...store.current, [slot]: before ?? 'no_audio' };
        return;
      }
      const id = ++playSeqRef.current;
      playingRef.current = { slot, id, target };
      setPlayingSlot(slot);
      if (target === 'recording' && mr && mr.state === 'recording') {
        try {
          mr.pause();
          pausedSinceRef.current = Date.now();
        } catch {
          // 一時停止できなければ、声が混ざらないよう流さない
          finishTopicAudio(id, 'failed');
          return;
        }
      }
      const a = ensureAudio();
      a.onended = () => finishTopicAudio(id, 'completed');
      a.onerror = () => finishTopicAudio(id, 'failed');
      a.src = url;
      try {
        const p = a.play();
        if (p && typeof p.catch === 'function') p.catch(() => finishTopicAudio(id, 'failed'));
      } catch {
        finishTopicAudio(id, 'failed');
      }
    },
    [ensureAudio, finishTopicAudio, stopTopicAudio]
  );

  const handleToggleTopicAudio = useCallback(
    (on: boolean) => {
      topicAudioMutedRef.current = !on;
      setTopicAudioMuted(!on);
      writeTopicAudioMuted(safeLocalStorage(), !on);
      if (!on) stopTopicAudio();
    },
    [stopTopicAudio]
  );

  // 画面を離れたら止める
  useEffect(
    () => () => {
      try {
        audioElRef.current?.pause();
      } catch {
        /* ignore */
      }
    },
    []
  );

  const stopRecording = useCallback(() => {
    // 流している途中なら、途中で止まった扱いにする（録音はこのまま確定するので再開しない）
    stopTopicAudio('interrupted', false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      try {
        if (mr.state === 'recording') mr.requestData();
      } catch {
        /* requestData 未対応の場合は無視 */
      }
      mr.stop();
    }
  }, [stopTopicAudio]);

  /**
   * keepTopic: 「もう少し話す」からの録り直し。お題を引き直さず、
   * 追い質問の文脈をそのまま保つ（課題 #60 ①）。
   */
  const startRecording = async (keepTopic = false) => {
    // 「もう少し話す」の画面で流していた音声は、途中で止まった扱いにして引き継ぐ
    stopTopicAudio('interrupted', false);
    // iPhone では、押した操作の中で一度鳴らしておかないと、後からお題の音声を自動で流せない（課題 #160）
    try {
      const a = ensureAudio();
      a.src = SILENCE_AUDIO_URL;
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
      /* 鳴らせなくても録音は続ける（お題の音声が流せなければ文字だけで出る） */
    }
    pausedTotalRef.current = 0;
    pausedSinceRef.current = null;
    audioOutcomesRef.current = keepTopic ? { ...retryOutcomesRef.current } : {};
    retryOutcomesRef.current = {};
    playedQuestionRef.current = null;
    // 録り直しでは、追い質問は直前の画面で流したので、カードでは流し直さない
    playedFollowUpRef.current = keepTopic ? topic?.topic_id ?? null : null;
    playedRetryRef.current = null;
    setError('');
    setBlob(null);
    setDurationSec(0);
    setVoiceStats(null);
    setSubmitted(false);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0;
      source.connect(analyser);
      analyser.connect(gainNode);
      gainNode.connect(audioContext.destination);
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
      setRecordingAnalyser(analyser);

      const mimeType = getRecordingMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        setRecordingAnalyser(null);
        try {
          audioContextRef.current?.close();
        } catch {
          /* ignore */
        }
        audioContextRef.current = null;
        stream.getTracks().forEach((t) => t.stop());
        // 一時停止していた時間（お題の音声を流していた間）は数えない（課題 #160）
        const duration = startTimeRef.current
          ? recordedMs(startTimeRef.current, pausedTotalRef.current, pausedSinceRef.current, Date.now()) / 1000
          : 0;
        const mime = recorder.mimeType || 'audio/webm';
        setDurationSec(Math.round(duration));
        // 一部ブラウザでは stop() 後の最後の ondataavailable が onstop より遅れて発火するため、
        // 短い遅延＋必要なら再試行で Blob を確定する
        const chunksAtStop = chunksRef.current.slice();
        const trySetBlob = () => {
          const finalChunks = chunksRef.current.length > 0 ? chunksRef.current : chunksAtStop;
          if (finalChunks.length > 0) {
            setBlob(new Blob(finalChunks, { type: mime }));
            return true;
          }
          return false;
        };
        setTimeout(() => {
          if (!trySetBlob()) {
            setTimeout(() => trySetBlob(), 200);
          }
        }, 150);
      };

      recorder.start(1000);
      startTimeRef.current = Date.now();
      setIsRecording(true);

      /*
       * お題は「録音開始の瞬間」にだけ取りに行く（課題 #60）。
       * 取得するたびに BE のプールを 1 件消費するため、画面表示時に先読みすると
       * 録音しなかった人の分まで消費してしまう。
       * 404（プール未投入）は正常系として扱い、カードを出さずに録音を続ける。
       *
       * ただし「もう少し話す」からの録り直しでは引き直さない。追い質問は
       * 直前のお題の続きなので、別のお題に変わると話がつながらなくなる。
       */
      setTopicVisible(false);
      setTopicDismissed(false);
      setRedrawUsed(false);
      /*
       * 「もう少し話す」からの録り直しは、直前の画面で追い質問を読んでいただいた
       * うえでの録音なので、最初から prompted 扱いにする（API #91 の topic_shown）。
       * カードがまだ出ていないからといって unprompted に数えると、
       * お題の影響を受けた録音を「受けていない」条件に混ぜてしまう。
       */
      setTopicShown(keepTopic);
      if (!keepTopic) {
        setTopic(null);
        setShowFollowUp(false);
        voiceTopicsApi
          .next(getToken())
          .then((data) => setTopic(data))
          .catch(() => {
            /* お題は録音の必須要素ではないので、取れなくても黙って続行する */
          });
      }

      timerRef.current = setInterval(() => {
        const elapsed =
          recordedMs(startTimeRef.current, pausedTotalRef.current, pausedSinceRef.current, Date.now()) / 1000;
        setDurationSec(Math.floor(elapsed));
        if (elapsed >= MAX_DURATION_SEC) stopRecording();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('record.errorMicrophone'));
    }
  };

  /*
   * 無音を監視してお題カードの出し入れを決める（課題 #60）。
   *
   *   無音 2 秒 → カードをフェードイン
   *   話し始めた → フェードアウト（読み続けさせない）
   *   話したあと 5 秒の無音 → 同じカードに追い質問を継ぎ足す
   *
   * 録音は止めない。声のデータが途切れないようにするため。
   */
  /*
   * 録音画面を開いている間だけ CSP 違反を監視する（API #63 の再発防止）。
   * 送信が `Failed to fetch` で落ちたとき、直前に connect-src 違反が出ていれば
   * 原因はほぼ確定する。
   */
  useEffect(() => startCspViolationWatch(), []);

  /*
   * 声が入っていた時間の割合を測る（課題 #98 段階 1）。お題の有無にかかわらず毎回測り、
   * 録音を止めた時点の値で送信前の案内を出すかを決める。
   * 音量が取れない環境では計測が進まず、案内も出ない（判定できないときは通す）。
   */
  useEffect(() => {
    if (!isRecording || !recordingAnalyser) return;
    const analyser = recordingAnalyser;
    const meter = createVoiceActivityMeter();
    const buf = new Uint8Array(analyser.fftSize);
    let rafId = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      rafId = requestAnimationFrame(tick);
      // お題の音声を流している間（録音を止めている間）は数えない（課題 #160）
      if (playingRef.current?.target === 'recording') {
        meter.skip(Date.now());
        return;
      }
      try {
        analyser.getByteTimeDomainData(buf);
      } catch {
        return;
      }
      meter.update(rmsFromTimeDomain(buf), Date.now());
    };
    tick();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      // 録音を止めたとき（isRecording が false になったとき）の値を残す
      setVoiceStats(meter.stats);
    };
  }, [isRecording, recordingAnalyser]);

  // お題の差し替え（引き直し）では監視を張り直さない。張り直すと無音の計測が
  // 0 に戻り、黙ったままなのにカードが 2 秒消えてしまうため。
  const hasTopic = topic !== null;
  useEffect(() => {
    if (!isRecording || !recordingAnalyser || !hasTopic) return;
    const analyser = recordingAnalyser;
    const tracker = createSilenceTracker();
    const buf = new Uint8Array(analyser.fftSize);
    let rafId = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      rafId = requestAnimationFrame(tick);
      // お題の音声を流している間は、流した声を本人の声と数えず、無音の時間も進めない（課題 #160）
      if (playingRef.current?.target === 'recording') {
        tracker.skip(Date.now());
        return;
      }
      try {
        analyser.getByteTimeDomainData(buf);
      } catch {
        return;
      }
      const { silentMs, hasSpoken } = tracker.update(rmsFromTimeDomain(buf), Date.now());
      // 一度出したら消さない。消すのは利用者が閉じたときと録音停止のときだけ
      if (silentMs >= TOPIC_SHOW_SILENCE_MS) {
        setTopicVisible(true);
        setTopicShown(true);
      }
      if (hasSpoken && silentMs >= TOPIC_FOLLOW_UP_SILENCE_MS) setShowFollowUp(true);
    };
    tick();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [isRecording, recordingAnalyser, hasTopic]);

  /** 「別のお題」。1 録音につき 1 回まで（BE は回数を制限しないので FE 側で抑える） */
  const handleRedraw = useCallback(() => {
    if (redrawUsed || redrawing) return;
    setRedrawing(true);
    voiceTopicsApi
      .next(getToken(), true)
      .then((data) => {
        setTopic(data);
        setShowFollowUp(false);
        setRedrawUsed(true);
      })
      .catch(() => {
        /* 引き直せなくても元のお題は出たままにする */
      })
      .finally(() => setRedrawing(false));
  }, [redrawUsed, redrawing, getToken]);

  /*
   * お題・追い質問の音声を流す（課題 #160。2026-10-09 高尾さん決定の A）。
   * カードが出たときにお題を、追い質問を足したときに追い質問を、自動で流す。
   * 「別のお題」で引き直したら、新しいお題を流す（前のお題は途中で止まった扱い）。
   */
  useEffect(() => {
    if (!isRecording || !topic || !topicVisible || topicDismissed) return;
    if (playedQuestionRef.current === topic.topic_id) return;
    playedQuestionRef.current = topic.topic_id;
    playTopicAudio('question', topic.question_audio_url, 'recording');
  }, [isRecording, topic, topicVisible, topicDismissed, playTopicAudio]);

  useEffect(() => {
    if (!isRecording || !topic || !topicVisible || topicDismissed || !showFollowUp || !topic.follow_up) return;
    if (playedFollowUpRef.current === topic.topic_id) return;
    playedFollowUpRef.current = topic.topic_id;
    playTopicAudio('followUp', topic.follow_up_audio_url, 'recording');
  }, [isRecording, topic, topicVisible, topicDismissed, showFollowUp, playTopicAudio]);

  /** 送信可能か（録音済み・20 秒以上・送信中でない・未送信）。ボタンの活性と強調表示に使う */
  const readyToSubmit = Boolean(blob) && !loading && !submitted && durationSec >= MIN_DURATION_SEC;
  /**
   * ほとんど声が入っていない録音か（課題 #98 段階 1）。送信前に気づかせるだけで、
   * 差し戻しはしない。録り直すか、このまま送信するかは本人が選ぶ。
   */
  const mostlySilent = readyToSubmit && isMostlySilent(voiceStats);
  /** 早めに止めた方に、追い質問を添えて録り直しを促す画面を出すか（課題 #60 ①） */
  const showRetry =
    Boolean(blob) && !isRecording && durationSec > 0 && durationSec < TOPIC_STOP_FOLLOW_UP_SEC && topic != null;

  /*
   * 「もう少し話す」の画面に出す問い（追い質問。無ければお題）も音声で流す（課題 #160）。
   * 録音していない間なので一時停止は要らない。流れ方は、次の録り直しの録音に引き継ぐ。
   */
  useEffect(() => {
    if (!showRetry || !topic) return;
    if (playedRetryRef.current === topic.topic_id) return;
    playedRetryRef.current = topic.topic_id;
    if (topic.follow_up) playTopicAudio('followUp', topic.follow_up_audio_url, 'retry');
    else playTopicAudio('question', topic.question_audio_url, 'retry');
  }, [showRetry, topic, playTopicAudio]);

  const handleSubmit = async () => {
    if (submitted) return;
    const token = getToken();
    if (!token) {
      setError(t('auth.loginFailed'));
      return;
    }
    if (!blob) {
      setError(t('record.errorNoRecording'));
      return;
    }
    if (durationSec < MIN_DURATION_SEC) {
      setError(t('record.errorTooShort'));
      return;
    }
    if (durationSec > MAX_DURATION_SEC) {
      setError(t('record.errorTooLong'));
      return;
    }

    setError('');
    setUploadFailureDetail(null);
    setAnalysisResult(null);
    setTranscriptText(null);
    setTranscriptChecked(false);
    setRecordingTranscribed(false);
    setSubmittedDurationSec(durationSec);
    setLoading(true);
    setSubmitPhase('upload');

    const idempotencyKey = crypto.randomUUID();
    const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('ogg') ? 'ogg' : 'mp4';
    const filename = `recording.${ext}`;
    // バックエンドはベースの MIME のみ許可するため、codecs 等を除く（例: audio/webm; codecs=opus → audio/webm）
    const contentType = blob.type ? blob.type.split(';')[0].trim() : undefined;
    const effectiveContentType = contentType || 'audio/webm';

    let presignRes: PresignResponse;
    try {
      presignRes = await filesApi.presign(
        {
          filename,
          content_type: contentType || undefined,
          size_bytes: blob.size,
        },
        token,
        idempotencyKey
      );
    } catch (err) {
      console.error('[record-submit] presign failed', err);
      setError(formatStageError('presign', err instanceof Error ? err.message : t('common.error')));
      setLoading(false);
      return;
    }

    try {
      // upload_url はバックエンドが返却した値をそのまま利用する（環境依存ホストに対応）
      const uploadRes = await uploadWithTimeout(
        presignRes.upload_url,
        blob,
        effectiveContentType,
        UPLOAD_TIMEOUT_MS
      );
      if (!uploadRes.ok) {
        throw new Error(`${t('record.errorUpload')} (status: ${uploadRes.status})`);
      }
    } catch (err) {
      /*
       * `Failed to fetch` は「HTTP のステータスが 1 つも返らなかった」ことしか示さず、
       * CSP でブロックされたのか CORS で落ちたのか届かなかったのかを区別できない。
       * 2026-09 の障害では、これだけを頼りに 3 日間 原因が分からなかった（API #63）。
       * 送信先ホストと CSP 違反の有無を添えて、その場で切り分けられるようにする。
       */
      const detail = await buildUploadFailureDetail(presignRes.upload_url);
      console.error('[record-submit] upload(put) failed', {
        error: err,
        uploadHost: detail?.host,
        cspBlocked: detail?.cspBlocked,
      });
      const uploadMessage =
        err instanceof DOMException && err.name === 'AbortError'
          ? `${t('record.errorUpload')} (timeout)`
          : err instanceof Error
          ? err.message
          : t('record.errorUpload');
      setError(formatStageError('upload', uploadMessage));
      setUploadFailureDetail(detail);
      setLoading(false);
      return;
    }

    try {
      await filesApi.complete(
        { file_key: presignRes.file_key, size_bytes: blob.size },
        token,
        idempotencyKey
      );
    } catch (err) {
      console.error('[record-submit] complete failed', err);
      setError(formatStageError('complete', err instanceof Error ? err.message : t('common.error')));
      setLoading(false);
      return;
    }

    setSubmitPhase('analyzing');

    try {
      const response: CreateVoiceJobResponse = await voiceJobsApi.createSync(
        {
          file_key: presignRes.file_key,
          duration: durationSec,
          locale: 'ja-JP',
          model_version: 'v1',
          /*
           * お題の利用状況（API #91）。日次の比較を同じ条件同士で行うために使う。
           * prompted / unprompted の判定は topic_shown で、topic_id の有無ではない。
           * お題を取得しても、詰まらなかった方の画面には出ないため。
           */
          ...(topic ? { topic_id: topic.topic_id } : {}),
          topic_shown: topicShown,
          topic_redrawn: redrawUsed,
          // お題・追い質問の音声の流れ方（課題 #160）。出さなかったものは送らない
          ...voiceJobAudioFields(audioOutcomesRef.current),
        },
        token,
        idempotencyKey
      );

      if (response.job.status === 'failed') {
        setError(formatStageError('voice_jobs', response.job.error_message || t('record.analysisFailed')));
        setLoading(false);
        return;
      }

      // ここまで来たら登録は済んでいる。同じ録音をもう一度送れないようにする
      setSubmitted(true);
      setRecordingTranscribed(response.transcription_enabled === true);
      if (response.recording?.analysis_json != null) {
        setAnalysisResult(response.recording.analysis_json);
        // 音声認識テキストは本人のみ閲覧可。BE が同梱してくれば表示
        setTranscriptChecked(true);
        if (typeof response.recording.transcript_text === 'string') {
          setTranscriptText(response.recording.transcript_text);
        }
      } else {
        navigate('/dashboard', { replace: true, state: { message: t('record.success') } });
      }
    } catch (err) {
      console.error('[record-submit] voice-jobs failed', err);
      if (err instanceof RequestError && err.errorCode === 'RECORDING_TOO_SHORT') {
        // BE が短すぎる録音を断ったとき（課題 #98。最短は BE の設定）。FE も 20 秒未満は送らないので、ふだんは出ない
        setError(t('record.errorTooShortServer'));
      } else {
        setError(formatStageError('voice_jobs', err instanceof Error ? err.message : t('record.errorJob')));
      }
    } finally {
      setLoading(false);
    }
  };

  const analysisSummaryText = getAnalysisSummaryText(analysisResult);
  const analysisTrendLabels = getAnalysisTrendLabels(analysisResult);

  return (
    <div className="page record-voice-page">
      <h1>{t('employee.recordVoice')}</h1>

      <p className="record-description">{t('record.description')}</p>
      <p className="record-privacy" role="note">
        {t('record.privacyNote')}
      </p>
      {transcriptionEnabled && (
        <p className="record-transcript-threshold" role="note">
          {t('record.transcriptThresholdHint', {
            min: MIN_DURATION_SEC,
            max: TRANSCRIPT_MAX_DURATION_SEC,
          })}
        </p>
      )}

      {error && (
        <div className="record-error" role="alert">
          {error}
          {/*
            送信が落ちたときの切り分け情報（API #63 の再発防止）。
            `Failed to fetch` だけでは CSP・CORS・到達不能を区別できないため、
            送信先ホストと CSP 違反の有無を添える。問い合わせを受けた側が
            この 1 行を見れば原因の当たりが付く。
          */}
          {uploadFailureDetail && (
            <p className="record-error-detail" data-testid="upload-failure-detail">
              {uploadFailureDetail.cspBlocked
                ? t('record.uploadDiag.cspBlocked', { host: uploadFailureDetail.host })
                : t('record.uploadDiag.host', { host: uploadFailureDetail.host })}
            </p>
          )}
        </div>
      )}

      <div className="record-actions">
        {!isRecording && (!blob || durationSec < MIN_DURATION_SEC) && (
          <button
            type="button"
            className="record-btn record-btn-start"
            onClick={() => startRecording()}
            disabled={loading}
          >
            {t('record.recordStart')}
          </button>
        )}
        {isRecording && recordingAnalyser && (
          <RecordingLevelMeter analyser={recordingAnalyser} />
        )}
        {/*
          お題カード（課題 #60）。無音 2 秒で現れ、話し始めると消える。
          録音は止めないので、読んでいる間も声は録れている。
          DOM に残したまま不透明度で出し入れするのは、表示のたびに
          読み上げ順が変わってスクリーンリーダー利用者を混乱させないため。
        */}
        {isRecording && topic && !topicDismissed && (
          <div
            className={`record-topic-card${topicVisible ? ' is-visible' : ''}`}
            data-testid="record-topic-card"
            role="status"
            aria-live="polite"
            aria-hidden={!topicVisible}
          >
            {/*
              カードの役割を明示する（課題 #60 案 1）。何の表示か分からないまま問いだけが
              出ると、答えなければならないものと受け取られやすい。参考として見られるものだと
              伝え、話している方には無視してよいものだと分かるようにする。
            */}
            <p className="record-topic-heading">{t('record.topic.heading')}</p>
            <p className="record-topic-question">{topic.question}</p>
            {showFollowUp && topic.follow_up && (
              <p className="record-topic-followup" data-testid="record-topic-followup">
                {topic.follow_up}
              </p>
            )}
            {/*
              お題の音声（課題 #160）。音声のあるお題だけ、AI で作った声であることと、
              音のオン・オフを出す（利用者が人の声と取り違えないように。高尾さん決定）
            */}
            {(topic.question_audio_url || (showFollowUp && topic.follow_up && topic.follow_up_audio_url)) && (
              <div className="record-topic-audio" data-testid="record-topic-audio">
                {playingSlot && (
                  <p className="record-topic-audio-playing" data-testid="record-topic-audio-playing">
                    <span aria-hidden="true">🔊 </span>
                    {t('record.topic.audioPlaying')}
                    <button
                      type="button"
                      className="record-topic-audio-stop"
                      onClick={() => stopTopicAudio()}
                      disabled={!topicVisible}
                      data-testid="record-topic-audio-stop"
                    >
                      {t('record.topic.audioStop')}
                    </button>
                  </p>
                )}
                <label className="record-topic-audio-toggle">
                  <input
                    type="checkbox"
                    checked={!topicAudioMuted}
                    onChange={(e) => handleToggleTopicAudio(e.target.checked)}
                    disabled={!topicVisible}
                    data-testid="record-topic-audio-toggle"
                  />{' '}
                  {t('record.topic.audioToggle')}
                </label>
                <p className="record-topic-audio-note">{t('record.topic.audioNote')}</p>
              </div>
            )}
            <div className="record-topic-actions">
              {!redrawUsed && (
                <button
                  type="button"
                  className="record-topic-redraw"
                  onClick={handleRedraw}
                  disabled={redrawing || !topicVisible}
                  data-testid="record-topic-redraw"
                >
                  {t('record.topic.redraw')}
                </button>
              )}
            {/*
                閉じるのは利用者の操作にする（課題 #60）。
                話し始めたら自動で消す作りだと、環境音で表示が揺れて読めない。
              */}
              <button
                type="button"
                className="record-topic-close"
                onClick={() => {
                  stopTopicAudio();
                  setTopicDismissed(true);
                }}
                disabled={!topicVisible}
                aria-label={t('common.close')}
                data-testid="record-topic-close"
              >
                ×
              </button>
            </div>
          </div>
        )}
        {isRecording && (
          <>
            <p className="recorded-info" aria-live="polite">
              {t('record.recordingLive', { seconds: durationSec })}
            </p>
            {playingSlot && (
              <p className="record-topic-audio-paused" role="status" data-testid="record-topic-audio-paused">
                <span aria-hidden="true">⏸ </span>
                {t('record.topic.audioPaused')}
              </p>
            )}
            <button
              type="button"
              className="record-btn record-btn-stop"
              onClick={stopRecording}
              aria-live="polite"
            >
              {t('record.recordStop')}
            </button>
          </>
        )}
        {blob && !isRecording && durationSec >= MIN_DURATION_SEC && (
          <p className="recorded-info">{t('record.recorded', { seconds: durationSec })}</p>
        )}
        {/* 基準値決め用の確認表示（課題 #98）。?voice_check=1 のときだけ出す */}
        {voiceCheck && voiceStats && !isRecording && (
          <p className="record-voice-check" data-testid="record-voice-check">
            {voiceStats.ratio != null
              ? t('record.voiceCheck.ratio', {
                  percent: Math.round(voiceStats.ratio * 100),
                  voiced: (voiceStats.voicedMs / 1000).toFixed(1),
                  measured: (voiceStats.measuredMs / 1000).toFixed(1),
                  threshold: Math.round(VOICED_RATIO_WARN_BELOW * 100),
                })
              : t('record.voiceCheck.notMeasured', {
                  measured: (voiceStats.measuredMs / 1000).toFixed(1),
                  min: MIN_MEASURED_MS / 1000,
                })}
          </p>
        )}
        {blob && !isRecording && durationSec > 0 && durationSec < MIN_DURATION_SEC && (
          <p className="record-too-short-hint" role="status">
            {t('record.tooShortHint', { seconds: durationSec })}
          </p>
        )}
        {/*
          早めに停止された方に、追い質問を添えて録り直しを促す（課題 #60 ①）。
          停止ボタンを押した操作は覆さない。促すだけで、従うかは本人が決める。
          お題は引き直さない（追い質問は直前のお題の続きのため）。
        */}
        {showRetry && topic && (
          <div className="record-topic-retry" role="status" data-testid="record-topic-retry">
            {/*
              追い質問が用意されていないお題もあるため（follow_up は任意）、
              その場合は元の問いをもう一度出す。ここを follow_up 必須にしていたせいで、
              実機では「もう少し話す」が出なかった（課題 #60 の報告）。
            */}
            <p className="record-topic-retry-question">{topic.follow_up || topic.question}</p>
            {(topic.follow_up ? topic.follow_up_audio_url : topic.question_audio_url) && (
              <p className="record-topic-audio-note">{t('record.topic.audioNote')}</p>
            )}
            <button
              type="button"
              className="record-btn record-btn-retry"
              onClick={() => startRecording(true)}
              disabled={loading}
              data-testid="record-topic-retry-btn"
            >
              {t('record.topic.speakMore')}
            </button>
          </div>
        )}
      </div>

      {/*
        「短いメモも残す（任意）」は、入力しても送信に含まれず保存されていなかった。
        書いた内容が知らないうちに消えるのを防ぐため、保存できるようになるまで出さない
        （課題 #145。2026-09-30 高尾さん依頼の暫定の対応。全テナント共通）。
      */}
      {SHOW_RECORD_MEMO && (
        <div className="record-memo">
          <label>
            <input
              type="checkbox"
              checked={showMemo}
              onChange={(e) => setShowMemo(e.target.checked)}
            />
            {t('record.memoLabel')}
          </label>
          {showMemo && (
            <input
              type="text"
              className="record-memo-input"
              placeholder={t('record.memoPlaceholder')}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              disabled={loading}
            />
          )}
        </div>
      )}

      {/*
        送信は自動化せず、明示的な操作として残す（課題 #34 / A 案・2026-09-01 決定）。
        音声は機微データであり、本人の意思表示なく送信すべきでないため。
        ただし「押す必要があると分からない」という指摘を受け、
        送信可能になったらボタンを強調し、押すまで送信されないことを明記する。
      */}
      <div className="record-submit-wrap">
        {/*
          ほとんど声が入っていないときの案内（課題 #98 段階 1）。差し戻さず、本人に選んでもらう。
          どちらかへ誘導しないよう、送信ボタンの強調も外す。
        */}
        {mostlySilent && (
          <div className="record-silent-warning" role="alert" data-testid="record-silent-warning">
            <p className="record-silent-warning-title">{t('record.silentWarning.title')}</p>
            <p className="record-silent-warning-body">{t('record.silentWarning.body')}</p>
            <button
              type="button"
              className="record-btn record-btn-start"
              onClick={() => startRecording()}
              data-testid="record-silent-retake"
            >
              {t('record.silentWarning.retake')}
            </button>
          </div>
        )}
        {readyToSubmit && !mostlySilent && (
          <p className="record-submit-hint" role="status">
            {t('record.notSentUntilSubmit')}
          </p>
        )}
        <button
          type="button"
          className={`record-btn record-btn-submit${readyToSubmit && !mostlySilent ? ' record-btn-submit-ready' : ''}`}
          onClick={handleSubmit}
          disabled={!readyToSubmit}
        >
          {loading
            ? submitPhase === 'analyzing'
              ? t('record.analyzing')
              : t('record.submitting')
            : submitted
            ? t('record.submitted')
            : mostlySilent
            ? t('record.silentWarning.sendAnyway')
            : t('record.submit')}
        </button>
      </div>

      {analysisResult != null && (
        <section className="record-result" aria-labelledby="record-result-heading">
          <h2 id="record-result-heading">{t('record.analysisResult')}</h2>
          <p className="record-analysis-summary">
            {/*
              コメントが返らないことがある。その状態で「完了しました」と言い切ると、
              ダッシュボードの「解析中」と食い違って、どちらが正しいか分からなくなる
              （課題 #99）。結果が無いときは受け付けた事実だけを伝える。
            */}
            {analysisSummaryText ?? t('record.analysisAcceptedNoSummary')}
          </p>
          {analysisTrendLabels.length > 0 && (
            <div className="record-analysis-trends">
              <h3>{t('record.analysisTrendTitle')}</h3>
              <ul className="record-analysis-trend-list" aria-label={t('record.analysisTrendTitle')}>
                {analysisTrendLabels.map((label) => (
                  <li key={label} className="record-analysis-trend-label">{label}</li>
                ))}
              </ul>
            </div>
          )}
            {/*
              取得できなかったときこそ開いておく。閉じていると見出しの下が空に見え、
              「テキストが出ない」という報告になる（課題 #99）。理由は畳まない。
            */}
          {recordingTranscribed && transcriptChecked && (
            <details className="record-transcript" open>
              <summary>{t('record.transcriptTitle')}</summary>
              <p className="record-transcript-hint">{t('record.transcriptHint')}</p>
              {transcriptText ? (
                <pre className="record-transcript-body">{transcriptText}</pre>
              ) : (
                <p className="record-transcript-missing">
                  {submittedDurationSec != null && submittedDurationSec > TRANSCRIPT_MAX_DURATION_SEC
                    ? t('record.transcriptMissingTooLong', {
                        min: MIN_DURATION_SEC,
                        max: TRANSCRIPT_MAX_DURATION_SEC,
                      })
                    : t('record.transcriptMissingGeneric')}
                </p>
              )}
            </details>
          )}
          <p className="record-back">
            {/* 今回の録音の結果の画面へ。まとめに入るまでは「解析中」と出す（#174） */}
            <Link to="/dashboard/score" className="record-btn record-btn-submit" data-testid="show-result">
              {t('dashboard.summaryWait.showResult')}
            </Link>
          </p>
        </section>
      )}

      <p className="record-back">
        <Link to="/dashboard" state={analysisResult != null ? { justSubmittedVoice: true } : undefined}>
          {t('common.back')} {t('record.backToDashboard')}
        </Link>
      </p>
    </div>
  );
}
